import { Monitor, Moon, Sun } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { useThemeEnabled } from '@apps/web/hooks/use_theme_enabled';
import { type Theme, themeStore, useTheme } from '@apps/web/lib/theme_store';

const THEME_OPTIONS: ReadonlyArray<{
    readonly value: Theme;
    readonly label: string;
    readonly icon: typeof Sun;
}> = [
    { value: 'light', label: 'Light theme', icon: Sun },
    { value: 'dark', label: 'Dark theme', icon: Moon },
    { value: 'system', label: 'Match system theme', icon: Monitor },
];

// Three-way light/dark/system picker; the selection is applied globally via
// the `dark` class on <html> and persisted per browser.
function ThemeToggleButtons() {
    const theme = useTheme();
    return (
        <div className="flex items-center gap-0.5">
            {THEME_OPTIONS.map((option) => (
                <Button
                    key={option.value}
                    type="button"
                    variant={theme === option.value ? 'secondary' : 'ghost'}
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => themeStore.setTheme(option.value)}
                    aria-label={option.label}
                    aria-pressed={theme === option.value}
                    title={option.label}
                >
                    <option.icon className="h-4 w-4" />
                </Button>
            ))}
        </div>
    );
}

// The "Theme" row rendered in the app sidebar footer, the settings sidebar
// footer, and the org menu. Platform admins and opted-in organizations can
// choose a theme; the app-level policy also covers collapsed/mobile sidebars.
export function ThemeToggleRow({
    variant,
}: {
    readonly variant: 'sidebar' | 'menu';
}) {
    const isEnabled = useThemeEnabled();

    if (!isEnabled) {
        return null;
    }
    if (variant === 'menu') {
        return (
            <Fragment>
                <div className="bg-border -mx-1 my-1 h-px" />
                <div className="flex items-center justify-between px-2 py-1.5">
                    <span className="text-sm">Theme</span>
                    <ThemeToggleButtons />
                </div>
            </Fragment>
        );
    }
    return (
        <div className="flex items-center justify-between px-2 py-1">
            <span className="text-muted-foreground text-sm">Theme</span>
            <ThemeToggleButtons />
        </div>
    );
}
